import { Modal } from '../../vendor/bootstrap';

/**
 * Wraps a Bootstrap modal (the `<x-ui.modal>` component). The modal markup is teleported to the
 * body, so it is looked up through `x-ref="modal"`.
 *
 * Open or close it from anywhere by dispatching `hawa-modal-open` / `hawa-modal-close` with `{ id }`.
 * Closing reports why (`backdrop`, `button`, `escape` or `programmatic`) through `closeEventName`
 * and the Livewire `hawa-modal:closed` event.
 *
 * @param {string} modalId
 * @param {string} closeEventName
 * @param {string} openEventName
 */
export default function bootstrapModal(modalId, closeEventName, openEventName) {
    return {
        modalId,
        closeReason: null,
        instance: null,

        init() {
            // Teleported markup can delay ref availability for one tick.
            this.$nextTick(() => {
                const element = this.$refs.modal ?? document.getElementById(modalId);

                if (!element) {
                    return;
                }

                this.instance = Modal.getOrCreateInstance(element);

                element.addEventListener('shown.bs.modal', () => {
                    window.dispatchEvent(new CustomEvent(openEventName, { detail: { id: modalId } }));
                });

                element.addEventListener('hide.bs.modal', () => {
                    this.closeReason ??= 'escape';
                });

                element.addEventListener('hidden.bs.modal', () => {
                    const payload = { id: modalId, reason: this.closeReason ?? 'programmatic' };

                    window.dispatchEvent(new CustomEvent(closeEventName, { detail: payload }));
                    window.Livewire?.dispatch('hawa-modal:closed', payload);

                    this.closeReason = null;
                });
            });
        },

        open() {
            this.instance?.show();
        },

        close() {
            if (!this.instance) {
                return;
            }

            // Release focus first so the browser does not warn about hiding a focused element.
            if (this.$refs.modal?.contains(document.activeElement)) {
                document.activeElement.blur();
            }

            this.markCloseReason('programmatic');
            this.instance.hide();
        },

        markCloseReason(reason) {
            this.closeReason = reason;
        },
    };
}
